import {
  enteredQuantities,
  remainingAfterEntry,
  addedAfterEntry,
  buildBatchesFromValues,
} from '@/components/common';
// Cross-slice on purpose: a fulfilment draws from declarations, and both forms pick them alike.
import { SELECT_INBOUND_CONFIG } from '@/slices/declarations/constants/selectInboundConfig';
import { SELECT_ORDER_CONFIG } from './selectOrderConfig';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { formatQuantityOrDash } from '@/lib/format';

// Outgoing shipments ▸ Fulfil — what <FulfillForm> needs to draw this slice's version.

const buildGroups = (t, shipment, values) => {
  const groups = [
    {
      heading: t('SECTION_GENERAL_DETAILS'),
      fields: [
        { label: t('FIELD_MATERIAL_NAME'), value: shipment.materialName },
        { label: t('FIELD_SOLD_TO'), value: shipment.soldTo },
        { label: t('FIELD_SHIPPING_SITES'), value: shipment.shippingTo },
        { label: t('FIELD_TRANSACTION_DATE'), value: shipment.transactionDate },
        { label: t('FIELD_DATE_OF_DISPATCH'), value: shipment.dateOfDispatch },
      ],
    },
  ];

  // Exactly one quantity row (working first, else declaration), as the order page does.
  const unitType = shipment.byUnit?.working ? UNIT_TYPES.working : UNIT_TYPES.declaration;
  const quantities = shipment.byUnit?.[unitType];
  if (quantities) {
    const unit = quantities.unit;
    const remaining = shipment.remainingByUnit?.[unit];
    const committed = shipment.committedByUnit?.[unit];
    const entered = enteredQuantities({ unit, unitType, values });
    groups.push({
      heading: t('SECTION_QUANTITY_IN_UNIT', { unit }),
      fields: [
        { label: t('FIELD_SHIPMENT_QUANTITY'), value: quantities.shipmentQuantity },
        {
          label: t('FIELD_ALLOCATED_QUANTITY'),
          value: formatQuantityOrDash(
            addedAfterEntry(committed?.allocated, entered.allocated),
            unit,
          ),
        },
        {
          label: t('FIELD_REMAINING_TO_ALLOCATE_QUANTITY'),
          value: formatQuantityOrDash(
            remainingAfterEntry(remaining?.toAllocate, entered.allocated),
            unit,
          ),
        },
      ],
    });
  }

  return groups;
};

const TILE_FIELDS = Object.freeze([
  { labelKey: 'ORDERS_FULFILL_SHIPMENT_QUANTITY_TILE', amount: 'shipmentQuantity' },
  {
    labelKey: 'ORDERS_FULFILL_ALLOCATED_QUANTITY_TILE',
    amount: 'allocatedQuantity',
    committed: 'allocated',
    entered: 'allocated',
  },
  {
    labelKey: 'ORDERS_FULFILL_REMAINING_TO_ALLOCATE_TILE',
    amount: 'remainingToAllocateQuantity',
    remaining: 'toAllocate',
    entered: 'allocated',
  },
]);

export const FULFILL_SHIPMENT_CONFIG = Object.freeze({
  buildGroups,
  sections: [
    {
      name: 'allocations',
      sectionHeadingKey: 'ORDERS_FULFILL_SECTION_ALLOCATIONS',
      rows: [{ slot: 'allocations', columns: 4 }],
    },
    SELECT_ORDER_CONFIG,
    {
      ...SELECT_INBOUND_CONFIG,
      tileFields: TILE_FIELDS,
      // A shipment fulfils the order it was pointed at, so that pick drives this picker.
      readPendingOrderId: (values) => values.pendingOrderId,
      // The inbound read is keyed by that order, so the button stays shut until one is picked.
      isDisabled: (record, values) =>
        SELECT_INBOUND_CONFIG.isDisabled(record) || !values.pendingOrderId,
      disabledNoteKey: 'OUTGOING_SHIPMENTS_FULFILL_ORDER_REQUIRED_FOR_CREDIT',
    },
  ],
  required: [
    { name: 'selectOrder', isMissing: (values) => !values.pendingOrderId },
    { name: 'selectInbound', isMissing: (values) => buildBatchesFromValues(values).length === 0 },
  ],
});
